<script setup>
import { computed } from "vue";
import { Link, usePage } from "@inertiajs/vue3";
import { useAuthUser } from "@/composables/useAuthUser.js";
import BrowserExtensionCta from "@/Components/Dashboard/BrowserExtensionCta.vue";
import IconFindFriends from "~/svg/nav-find-friends.svg";
import IconFollowing from "~/svg/nav-following.svg";
import IconFollowers from "~/svg/nav-followers.svg";

const user = useAuthUser();

const items = [
    {
        label: "Find Friends",
        routeName: "friends.find-friends",
        component: "Friends/FindFriends",
        icon: IconFindFriends,
    },
    {
        label: "Following",
        routeName: "friends.index",
        component: "Friends/Following",
        icon: IconFollowing,
    },
    {
        label: "Followers",
        routeName: "friends.followers",
        component: "Friends/Followers",
        icon: IconFollowers,
    },
];

const currentComponent = computed(() => usePage().component);
</script>

<template>
    <div>
        <!-- Desktop sidebar: cream panel overlapping the hero band -->
        <!-- max-md:hidden (not `hidden md:flex`): legacy .hidden is !important on pages that load app.scss -->
        <aside
            class="relative z-10 flex h-full flex-col rounded-brand bg-brand-cream p-6 max-md:hidden"
        >
            <h2 class="font-brand text-[22px] font-semibold text-brand-navy">
                {{ user?.full_name }}&rsquo;s Friends
            </h2>

            <nav class="mt-5">
                <ul class="flex flex-col gap-1">
                    <li v-for="item in items" :key="item.routeName">
                        <Link
                            :href="route(item.routeName)"
                            class="flex items-center gap-4 rounded-brand px-4 py-4 font-brand text-[15px] font-bold !text-brand-navy no-underline transition"
                            :class="
                                currentComponent === item.component
                                    ? 'bg-white shadow-sm'
                                    : 'hover:bg-white/60'
                            "
                        >
                            <!-- Red only while active, navy at rest — same
                                 convention as the Manage Account nav (GH-922). -->
                            <component
                                :is="item.icon"
                                class="h-5 w-6 shrink-0 transition-colors"
                                :class="
                                    currentComponent === item.component
                                        ? 'text-brand-red'
                                        : 'text-brand-navy'
                                "
                            />
                            {{ item.label }}
                        </Link>
                    </li>
                </ul>
            </nav>

            <div class="mt-auto pt-10">
                <BrowserExtensionCta />
            </div>
        </aside>

        <!-- Mobile: horizontal tabs with active underline -->
        <nav
            class="-mx-4 mt-4 border-b border-brand-tan/60 px-4 md:hidden"
            aria-label="Friends"
        >
            <ul class="flex items-stretch justify-between gap-2">
                <li v-for="item in items" :key="item.routeName">
                    <Link
                        :href="route(item.routeName)"
                        class="flex items-center gap-2 border-b-4 pb-3 font-brand text-[15px] font-bold !text-brand-navy no-underline"
                        :class="
                            currentComponent === item.component
                                ? 'border-[#ffaa4a]'
                                : 'border-transparent'
                        "
                    >
                        <component
                            :is="item.icon"
                            v-if="currentComponent === item.component"
                            class="h-4 w-5 shrink-0 text-brand-red"
                        />
                        {{ item.label }}
                    </Link>
                </li>
            </ul>
        </nav>
    </div>
</template>
